/**
 * Содержимое материалов по типам. Проверка структуры мягкая (редакторы развиваются, новые поля оформления
 * не должны ломать сохранение), но обязательная для безопасности: URL, глубина, размеры.
 * Связи и текст для поиска сервер извлекает из содержимого сам — клиенту не доверяет.
 */
import { z } from 'zod';
export const RESOURCE_KINDS = ['folder', 'doc', 'mindmap', 'board', 'task'];
export const CONTENT_LIMITS = {
    /** Сериализованное содержимое, байт. */
    maxBytes: 2_000_000,
    maxDepth: 48,
    docBlocks: 5_000,
    mapNodes: 2_000,
    mapEdges: 4_000,
    boardNodes: 5_000,
    boardEdges: 5_000,
};
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Ключи, значения которых — ссылки (картинки, обложки, закладки, встраивания). */
const URL_KEYS = /^(url|href|src|cover|image|imageUrl|link|embedUrl|poster)$/i;
/** Ключи-ссылки на другие материалы (`links` — массив id в документах прототипа). */
const REF_KEYS = /^(rid|refId|src|targetId|links)$/;
const TEXT_KEYS = /^(text|label|title|body|lead|caption|question|answer)$/;
/** Разрешены http(s), mailto, относительные пути и якоря, data: только для картинок. */
export function isSafeUrl(value) {
    const v = value.trim();
    if (v === '' || v.startsWith('/') || v.startsWith('#'))
        return !v.startsWith('//');
    if (/^data:image\/(png|jpe?g|gif|webp|avif);base64,/i.test(v))
        return true;
    if (UUID_RE.test(v))
        return true; // src синхронного блока — id материала, а не URL
    if (/^#[0-9a-f]{3,8}$/i.test(v))
        return true; // обложка-цвет
    try {
        const u = new URL(v);
        return u.protocol === 'https:' || u.protocol === 'http:' || u.protocol === 'mailto:';
    }
    catch {
        return false;
    }
}
/** Обход JSON: глубина и безопасные URL. */
export function checkContentSafety(value) {
    const issues = [];
    const walk = (v, path, depth, key) => {
        if (issues.length > 20)
            return;
        if (depth > CONTENT_LIMITS.maxDepth) {
            issues.push({ path, message: 'Слишком глубокая вложенность' });
            return;
        }
        if (typeof v === 'string') {
            if (URL_KEYS.test(key) && !isSafeUrl(v))
                issues.push({ path, message: 'Недопустимая ссылка' });
            return;
        }
        if (Array.isArray(v))
            v.forEach((x, i) => walk(x, `${path}[${i}]`, depth + 1, key));
        else if (v && typeof v === 'object')
            for (const [k, x] of Object.entries(v))
                walk(x, path ? `${path}.${k}` : k, depth + 1, k);
    };
    walk(value, '', 0, '');
    return issues;
}
const id = z.string().min(1).max(100);
const point = z.object({ x: z.number().finite().min(-1e6).max(1e6), y: z.number().finite().min(-1e6).max(1e6) });
const edge = z.looseObject({ id, source: id, target: id, label: z.string().max(500).optional() });
const nodeData = z.looseObject({
    label: z.string().max(2_000).default(''),
    text: z.string().max(20_000).optional(),
    refId: z.uuid().optional(),
});
const graphNode = z.looseObject({ id, position: point, data: nodeData });
/** Проверка рёбер: без петель и ссылок на несуществующие узлы. */
function checkEdges(ctx, nodes, edges) {
    const ids = new Set(nodes.map((n) => n.id));
    if (ids.size !== nodes.length)
        ctx.addIssue({ code: 'custom', message: 'Повторяющиеся id узлов', path: ['nodes'] });
    edges.forEach((e, i) => {
        if (e.source === e.target)
            ctx.addIssue({ code: 'custom', message: 'Ребро-петля', path: ['edges', i] });
        else if (!ids.has(e.source) || !ids.has(e.target))
            ctx.addIssue({ code: 'custom', message: 'Ребро к несуществующему узлу', path: ['edges', i] });
    });
}
const block = z.lazy(() => z.looseObject({
    id: z.string().max(100).optional(),
    type: z.string().min(1).max(64),
    props: z.record(z.string(), z.unknown()).optional(),
    content: z.unknown().optional(),
    children: z.array(block).max(CONTENT_LIMITS.docBlocks).optional(),
}));
export const contentSchemas = {
    folder: z.looseObject({ icon: z.string().max(64).optional(), color: z.string().max(16).optional() }),
    doc: z.looseObject({
        schemaVersion: z.literal(1).default(1),
        blocks: z.array(block).max(CONTENT_LIMITS.docBlocks).default([]),
        page: z.record(z.string(), z.unknown()).optional(),
    }),
    mindmap: z
        .looseObject({
        schemaVersion: z.literal(1).default(1),
        style: z.record(z.string(), z.unknown()).optional(),
        nodes: z.array(graphNode).max(CONTENT_LIMITS.mapNodes).default([]),
        edges: z.array(edge).max(CONTENT_LIMITS.mapEdges).default([]),
        groups: z
            .array(z.looseObject({ id, nodes: z.array(id).max(CONTENT_LIMITS.mapNodes) }))
            .max(500)
            .optional(),
        summaries: z
            .array(z.looseObject({ id, nodes: z.array(id).max(CONTENT_LIMITS.mapNodes) }))
            .max(500)
            .optional(),
        rels: z.array(edge).max(1_000).optional(),
    })
        .superRefine((c, ctx) => checkEdges(ctx, c.nodes, c.edges)),
    board: z
        .looseObject({
        schemaVersion: z.literal(1).default(1),
        style: z.record(z.string(), z.unknown()).optional(),
        nodes: z.array(graphNode).max(CONTENT_LIMITS.boardNodes).default([]),
        edges: z.array(edge).max(CONTENT_LIMITS.boardEdges).default([]),
    })
        .superRefine((c, ctx) => checkEdges(ctx, c.nodes, c.edges)),
    task: z.object({
        schemaVersion: z.literal(1).default(1),
        text: z.string().max(20_000).default(''),
        status: z.enum(['todo', 'doing', 'done']).default('todo'),
        priority: z.enum(['low', 'medium', 'high']).default('medium'),
        assignees: z.array(z.uuid()).max(20).default([]),
        dueDate: z
            .string()
            .regex(/^(\d{4}-\d{2}-\d{2})?$/, 'Дата в формате YYYY-MM-DD')
            .default(''),
        /** Порядок в колонке канбана — дробный индекс (fractional-indexing): строки сравниваются как есть. */
        rank: z
            .string()
            .max(64)
            .regex(/^[0-9A-Za-z]*$/, 'Ранг — цифры и латинские буквы')
            .default(''),
    }),
};
/** Проверить и нормализовать содержимое (значения по умолчанию, лимиты, безопасность). */
export function parseContent(kind, raw) {
    const size = JSON.stringify(raw ?? {}).length;
    if (size > CONTENT_LIMITS.maxBytes)
        return { ok: false, issues: [{ path: '', message: 'Материал слишком большой' }] };
    const safety = checkContentSafety(raw);
    if (safety.length)
        return { ok: false, issues: safety };
    const parsed = contentSchemas[kind].safeParse(raw ?? {});
    if (!parsed.success) {
        return {
            ok: false,
            issues: parsed.error.issues.slice(0, 20).map((i) => ({ path: i.path.join('.'), message: i.message })),
        };
    }
    return { ok: true, content: parsed.data };
}
/** id материалов, на которые ссылается содержимое (упоминания, виджеты, узлы карт, синхронные блоки). */
export function extractRefs(content) {
    const refs = new Set();
    const walk = (v, key, depth) => {
        if (depth > CONTENT_LIMITS.maxDepth)
            return;
        if (typeof v === 'string') {
            if (REF_KEYS.test(key) && UUID_RE.test(v))
                refs.add(v.toLowerCase());
        }
        else if (Array.isArray(v))
            v.forEach((x) => walk(x, key, depth + 1));
        else if (v && typeof v === 'object')
            for (const [k, x] of Object.entries(v))
                walk(x, k, depth + 1);
    };
    walk(content, '', 0);
    return refs;
}
/** Плоский текст для поиска и превью. */
export function extractText(content, limit = 100_000) {
    const parts = [];
    let length = 0;
    const walk = (v, key, depth) => {
        if (length >= limit || depth > CONTENT_LIMITS.maxDepth)
            return;
        if (typeof v === 'string') {
            if (TEXT_KEYS.test(key) && v.trim()) {
                parts.push(v.trim());
                length += v.length + 1;
            }
        }
        else if (Array.isArray(v))
            v.forEach((x) => walk(x, key, depth + 1));
        else if (v && typeof v === 'object')
            for (const [k, x] of Object.entries(v))
                walk(x, k, depth + 1);
    };
    walk(content, '', 0);
    return parts.join(' ').replace(/\s+/g, ' ').slice(0, limit);
}
export const excerptOf = (text, max = 240) => text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
/** Краткие поля для снимка проекта (канбан, иконки) — без загрузки полного содержимого. */
export function contentMeta(kind, content) {
    switch (kind) {
        case 'task': {
            const t = content;
            return { status: t.status, priority: t.priority, assignees: t.assignees, dueDate: t.dueDate, rank: t.rank };
        }
        case 'doc': {
            const page = (content.page ?? {});
            return {
                icon: page.icon ?? null,
                tags: Array.isArray(page.tags) ? page.tags.slice(0, 10) : [],
                status: page.status ?? null,
            };
        }
        case 'mindmap':
        case 'board':
            return { nodeCount: Array.isArray(content.nodes) ? content.nodes.length : 0 };
        default:
            return {};
    }
}
/**
 * Упоминания людей в документе (inline `mention` с `kind: 'user'`): id → текст блока, где упомянули
 * (для «Входящих» и письма). Упоминания материалов сюда не входят — это ссылки (extractRefs).
 */
export function extractUserMentions(content) {
    const out = new Map();
    const inlineText = (items) => items
        .map((x) => {
        const o = x;
        if (typeof o?.text === 'string')
            return o.text;
        if (o?.type === 'mention')
            return typeof o.props?.label === 'string' ? '@' + o.props.label : '';
        if (o?.type === 'link' && Array.isArray(o.content))
            return inlineText(o.content);
        return '';
    })
        .join('');
    const walk = (v, depth) => {
        if (depth > CONTENT_LIMITS.maxDepth)
            return;
        if (Array.isArray(v))
            v.forEach((x) => walk(x, depth + 1));
        else if (v && typeof v === 'object') {
            const o = v;
            if (Array.isArray(o.content)) {
                for (const x of o.content) {
                    const m = x;
                    if (m?.type === 'mention' && m.props?.kind === 'user' && typeof m.props.rid === 'string') {
                        const id = m.props.rid.toLowerCase();
                        if (UUID_RE.test(id) && !out.has(id))
                            out.set(id, inlineText(o.content));
                    }
                }
            }
            for (const x of Object.values(v))
                walk(x, depth + 1);
        }
    };
    walk(content, 0);
    return out;
}
function eachBlock(content, fn) {
    const walk = (list, depth) => {
        if (!Array.isArray(list) || depth > CONTENT_LIMITS.maxDepth)
            return;
        for (const b of list) {
            if (!b || typeof b !== 'object')
                continue;
            fn(b);
            walk(b.children, depth + 1);
        }
    };
    walk(content?.blocks, 0);
}
/**
 * Связи «пункт чек-листа → материал» в документе: пункт, в котором упомянут материал (`@задача`).
 * Задача это или нет, решает сервер: здесь только id из упоминания.
 */
export function extractTaskLinks(content) {
    const out = [];
    eachBlock(content, (b) => {
        if (b.type !== 'checkListItem' || typeof b.id !== 'string' || !Array.isArray(b.content))
            return;
        const m = b.content.find((x) => x?.type === 'mention' && x.props?.kind === 'res' && typeof x.props.rid === 'string');
        const rid = typeof m?.props?.rid === 'string' ? m.props.rid.toLowerCase() : '';
        if (UUID_RE.test(rid))
            out.push({ blockId: b.id, taskId: rid, checked: b.props?.checked === true });
    });
    return out;
}
/** Отметить пункты чек-листа (blockId → checked) в JSON документа. Возвращает новое содержимое. */
export function setChecks(content, checks) {
    const next = structuredClone(content);
    eachBlock(next, (b) => {
        if (b.type === 'checkListItem' && typeof b.id === 'string' && checks.has(b.id))
            b.props = { ...(b.props ?? {}), checked: checks.get(b.id) };
    });
    return next;
}
//# sourceMappingURL=index.js.map